import { useLayoutEffect, useState, useId, Fragment } from "react"
import { mergeProps } from "react-aria"
import { motion, useAnimationControls } from "framer-motion"
import parse from "html-react-parser"

import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useLocale } from "contexts/Locale"
import { useUser } from "contexts/User"

import camelCase from "utils/camelCase"

import styled from "@emotion/styled"
import hash from "@emotion/hash"

import { fullGrid } from "css/grid"
import { headline50, headline30, legend100, body50, bold } from "css/text"
import getMediaQuery from "css/breakpoints"
import { buttonInline, green } from "css/buttons"

import { getPriceDatas } from "components/price/utils"
import Image from "components/media/ImageCLD"
import Link from "components/link/Link"
import VideoLauncher from "components/video-launcher/VideoLauncher"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"
import GlossaryBtn from "components/glossary/GlossaryBtn"
import P13N from "../perso"

const cssCollapsed = `css-${hash("collapsed")}`
const cssExpanded = `css-${hash("expanded")}`
const cssOneColumn = `css-${hash("one-column")}`
const cssOrphans = `css-${hash("orphans")}`

const Root = styled.div`
  ${fullGrid}
  padding-block: 10vh;
  background-color: white;

  > div {
    grid-column: main;
    grid-row: 1;

    ${getMediaQuery("m")} {
      grid-column: col 2 / span 4;
    }
  }

  figure {
    grid-column: col 2 / span 4;
    grid-row: 2;
    justify-self: center;
    margin-block: -15%;

    ${getMediaQuery("m")} {
      grid-column: col 5 / span 4;
      grid-row: 1;
      margin-block-start: 0;
    }

    img {
      display: block;
    }
  }
`

const Title = styled.h3`
  ${headline50}
  margin-block-end: 1rem;

  span {
    display: flex;
  }

  > * {
    color: rgb(var(--green));
  }
`

const Reference = styled.p`
  ${legend100}

  b {
    ${bold}
  }
`

const List = styled.ul`
  grid-column: main;
  position: relative;

  > li {
    border-top: 1px solid rgb(var(--grey));

    :last-of-type {
      border-bottom: 1px solid rgb(var(--grey));
    }
  }

  ${getMediaQuery("m")} {
    grid-column: col 2 / span 10;
  }
`

const Header = styled.h4`
  ${headline30}

  button {
    all: unset;
    align-items: center;
    cursor: pointer;
    display: flex;
    height: 4.375rem;
    justify-content: space-between;
    transition: color 0.3s;
    width: 100%;

    @media (hover: hover) {
      :hover {
        color: rgb(var(--green));
      }
    }
  }
`

const Icon = styled.span`
  --iconSize: 12px;
  --iconStrokeWidth: 2px;

  ::before,
  ::after {
    background: currentColor;
    content: "";
    display: block;
    height: var(--iconStrokeWidth);
    left: 0;
    position: absolute;
    right: 0;
    top: 50%;
    transition: transform 0.6s;
    will-change: transform;

    html.prefers-reduced-motion & {
      transition: none;
    }
  }

  .${cssCollapsed} & {
    ::after {
      transform: rotate(90deg);
    }
  }

  height: var(--iconSize);
  position: relative;
  width: var(--iconSize);

  .no-js & {
    display: none;
  }
`

const Panel = styled(motion.div)`
  overflow: hidden;

  ::before,
  ::after {
    column-span: all;
    content: "";
    display: block;
  }

  ::before {
    height: 1.5rem;

    ${getMediaQuery("m")} {
      height: 2rem;
    }
  }

  ::after {
    height: 3rem;

    ${getMediaQuery("m")} {
      height: 4rem;
    }
  }
`

const SubPanel = styled(motion.ul)`
  /*&[hidden] {
     display: block; // override default
  }*/

  columns: 2;
  column-gap: var(--grid-gap);
  overflow: hidden;
  will-change: height, opacity, transform;

  &.${cssOneColumn} {
    columns: unset;

    li {
      padding-inline-end: 0;
    }
  }

  &.${cssOrphans} {
    orphans: 1;
  }

  ${getMediaQuery("xl")} {
    column-count: 3;
  }

  li {
    break-inside: avoid;
    padding-inline-end: calc(var(--grid-gap) * 2);

    // Safari
    box-sizing: border-box;
    display: inline-block;
    width: 100%;

    &:not(:last-of-type) {
      margin-block-end: 1rem;
    }
  }

  h5 {
    ${body50}
    ${bold}
  }

  p {
    ${body50}
    hyphens: auto;
    text-wrap: pretty;

    ${getMediaQuery("m", { max: true })} {
      br {
        display: none;
      }
    }
  }

  ${getMediaQuery("m")} {
    padding-inline-end: calc(var(--grid-col-unit) + var(--grid-gap));
  }
`

const InlineLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1ex 10vw;
  margin-block-start: 2ex;
`

const CTAVideo = styled(VideoLauncher)`
  ${buttonInline}
  ${green}
`

// TODO: MEMO
function UserGuides(props) {
  const { text, user_guide_link, path_cl: user_guide_url, code, modelName, rmc, video_player_cld, isOpen } = props

  const locale = useLocale()
  const env = useEnv()
  const { countryCode } = useUser()

  const lang = locale?.current?.codes?.www

  const linkProps = mergeProps(user_guide_link, { icon: "download", external: user_guide_url?.replace("$lang", lang), style: "inline green" })

  const triggerWaEvent = async () => {
    const { price, currency } = await getPriceDatas({ rmc, countryCode, locale, env })

    waTriggerEvent({
      eventName: "download",
      eventType: "Click",
      products: [
        {
          productCategory: code,
          productModel: modelName,
          productID: rmc,
          productQuantity: "1",
          productPrice: price,
          currency: currency,
        },
      ],
      downloadInfo: {
        fileName: linkProps.external,
        type: "userguide",
        position: "model page",
        size: "",
      },
    })
  }

  return (
    <li>
      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
      <p dangerouslySetInnerHTML={{ __html: text }} />
      <InlineLinks>
        {!!video_player_cld?.sources?.length && <CTAVideo video_player_cld={video_player_cld} icon='play' />}

        <Link tabIndex={isOpen ? 0 : -1} {...linkProps} onClick={triggerWaEvent}>
          {user_guide_link?.label}
        </Link>
      </InlineLinks>
    </li>
  )
}

function Section({ title, content, user_guides, legal_agec, id }) {
  const uid = useId()
  const [isOpen, setIsOpen] = useState(false)

  const controls = useAnimationControls()
  const prefersReducedMotion = process.browser && document.documentElement.classList.contains("prefers-reduced-motion")

  const expand = async () => {
    await controls.start({ height: "auto", visibility: "visible" })
    return await controls.start({ opacity: 1, translateY: 0 })
  }

  const collapse = async () => {
    await controls.start({ opacity: 0, translateY: "-5%" })
    return await controls.start({ height: 0, visibility: "hidden" })
  }

  useLayoutEffect(() => {
    if (isOpen) expand()
    else collapse()
  }, [isOpen])

  const transformHtml = html => {
    return parse(html, {
      replace: node => {
        if (node.name === "spec-term") {
          const content = node.children[0].data
          return <GlossaryBtn {...node.attribs}>{content}</GlossaryBtn>
        }
      },
    })
  }

  return (
    <li>
      <Header className={isOpen ? cssExpanded : cssCollapsed}>
        <button type='button' id={`${uid}id`} aria-expanded={isOpen} aria-controls={`${uid}`} onClick={() => setIsOpen(!isOpen)}>
          {title}
          <Icon aria-hidden='true' />
        </button>
      </Header>
      <Panel
        animate={controls}
        aria-labelledby={`${uid}id`}
        aria-hidden={!isOpen}
        id={`${uid}`}
        // index={index} /** TODO SSR hydration error! but doesn't seem to do anything */
        transition={{ duration: prefersReducedMotion ? 0 : 0.3 }}
      >
        <SubPanel
          className={content.length === 1 || content === "ug" ? cssOneColumn : content.filter(item => item.text !== "").length === 2 ? cssOrphans : null}
        >
          {content === "ug" ? (
            <UserGuides isOpen={isOpen} {...user_guides} />
          ) : (
            content?.map(({ id, title, text }) => {
              return (
                <Fragment key={id}>
                  {text && (
                    <li>
                      {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
                      {title && <h5 dangerouslySetInnerHTML={{ __html: title }} />}
                      <p>{transformHtml(text)}</p>
                    </li>
                  )}
                </Fragment>
              )
            })
          )}
        </SubPanel>
        {id === 1 ? <P13N legal_agec={legal_agec} /> : null}
      </Panel>
    </li>
  )
}

export default function ModelSpecifications({ dictionary, user_guides, legal_agec, ...props }) {
  const console = useConsole()
  const { catalogYear } = useEnv()

  console.log("•••••••• props", props)

  // TODO
  const translation = new Proxy(
    (dictionary || []).reduce((acc, { translation: { key, value } }) => {
      acc[camelCase(key)] = (...variables) => {
        let translatedAs = value
        for (const [i, variable] of variables.entries()) translatedAs = translatedAs.replaceAll(`{{${i + 1}}}`, variable)
        return translatedAs
      }

      return acc
    }, {}),
    {
      get: (t, k) => {
        if (t.hasOwnProperty(k)) return t[k]
        else return () => `%MISSING(${k})%`
      },
    }
  )

  const data = {
    watch: props.familyNode.name,
    sources: [{ public_id: `catalogue/${catalogYear}/upright-c/${props.rmc}`, width: "800", height: "1180", policy: "t_v7-majesty" }],
    shadow: [{ public_id: `catalogue/${catalogYear}/upright-c-shadow/${props.rmc}`, width: "800", height: "1380", policy: "t_v7-majesty-shadow" }],
    reference: props.reference_code,
    specifications: [
      {
        id: 0,
        title: translation.modelCase(),
        content: [
          { id: 0, title: translation.modelCase(), text: props.case?.labels?.title },
          { id: 1, title: translation.oysterArchitecture(), text: props.case?.labels?.oyster_architecture },
          { id: 2, title: translation.diameter(), text: props.case?.labels?.diameter },
          { id: 3, title: translation.material(), text: props.case?.labels?.material },
          { id: 4, title: translation.bezel(), text: props.case?.labels?.bezel },
          { id: 5, title: translation.windingCrown(), text: props.case?.labels?.winding_crown },
          { id: 6, title: translation.crystal(), text: props.case?.labels?.crystal },
          { id: 7, title: translation.caseBack(), text: props.case?.labels?.case_back },
          { id: 8, title: translation.waterResistance(), text: props.case?.labels?.water_resistance },
        ],
      },
      {
        id: 1,
        title: translation.movement(),
        content: [
          { id: 0, title: translation.movement(), text: props.movement?.labels?.title },
          { id: 1, title: translation.calibre(), text: props.movement?.labels?.calibre },
          { id: 2, title: translation.precisionStatic(), text: props.movement?.labels?.precision_static },
          { id: 3, title: translation.functions(), text: props.movement?.labels?.functions },
          { id: 4, title: translation.oscillator(), text: props.movement?.labels?.oscillator },
          { id: 5, title: translation.winding(), text: props.movement?.labels?.winding },
          { id: 6, title: translation.autonomy(), text: props.movement?.labels?.autonomy },
        ],
        legal_agec: legal_agec,
      },
      {
        id: 2,
        title: translation.bracelet(),
        content: [
          { id: 0, title: translation.bracelet(), text: props.bracelet?.labels?.title },
          { id: 1, title: translation.material(), text: props.bracelet?.labels?.material },
          { id: 2, title: translation.claspType(), text: props.bracelet?.labels?.clasp_type },
        ],
      },
      {
        id: 3,
        title: translation.dial(),
        content: [
          { id: 0, title: translation.dial(), text: props.dial?.labels?.title },
          { id: 1, title: translation.gemSettings(), text: props.dial?.labels?.gem_settings },
          { id: 2, title: translation.details(), text: props.dial?.labels?.details },
        ],
      },
      {
        id: 4,
        title: translation.certification(),
        content: [{ id: 0, title: translation.certification(), text: props.movement?.labels?.certification }],
      },
      {
        id: 5,
        title: user_guides.title,
        content: "ug",
        user_guides: {
          ...user_guides,
          ...props.editorial_mapping.userguide,
          video_player_cld: props.editorial_mapping.videoHowTo_v7_cl,
          code: props.familyNode.code,
          modelName: props.nameCode,
          rmc: props.rmc,
        },
      },
    ],
  }

  console.verbose("Model:ModelSpecifications(%o)", props)
  return (
    <Root>
      <div>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
        <Title dangerouslySetInnerHTML={{ __html: `${translation.specificationsTitle(data?.watch)}` }} />
        <Reference>
          <b>{translation.reference()}</b> {data?.reference}
        </Reference>
      </div>
      <figure>
        <Image sources={data?.shadow} sizes='(min-width: 48rem) 33vw, 50vw' loading='lazy' />
      </figure>
      <figure>
        <Image sources={data?.sources} sizes='(min-width: 48rem) 33vw, 50vw' loading='lazy' />
      </figure>
      <List>
        {data?.specifications.map((props, index) => (
          <Section key={props.id} index={index} {...props} />
        ))}
      </List>
    </Root>
  )
}
